import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { PageResponse, Take } from '@/lib/types';

export function useArrangementTakes(
  arrangementId: string,
): UseQueryResult<PageResponse<Take>> {
  return useQuery({
    queryKey: ['arrangements', arrangementId, 'takes'],
    queryFn: () => api.fetch<PageResponse<Take>>(`/repertoire/${arrangementId}/takes`),
    enabled: Boolean(arrangementId),
  });
}

export type CreateTakePayload = {
  blob: Blob;
  recordedAt: Date;
  durationSeconds: number;
  labelCanonicalName: string;
  tempoBpm?: number | null;
  notes?: string | null;
  practiceSessionId?: string | null;
};

export function useCreateTake(
  arrangementId: string,
): UseMutationResult<Take, Error, CreateTakePayload> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateTakePayload) => {
      const metadata = {
        recordedAt: payload.recordedAt.toISOString(),
        durationSeconds: payload.durationSeconds,
        labelCanonicalName: payload.labelCanonicalName,
        tempoBpm: payload.tempoBpm ?? null,
        notes: payload.notes ?? null,
        practiceSessionId: payload.practiceSessionId ?? null,
      };

      const formData = new FormData();
      formData.append('audio', payload.blob, 'take.webm');
      formData.append(
        'metadata',
        new Blob([JSON.stringify(metadata)], { type: 'application/json' }),
      );

      const token = api.getToken();
      const response = await fetch(`/api/repertoire/${arrangementId}/takes`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to save take (HTTP ${response.status})`);
      }

      return (await response.json()) as Take;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['arrangements', arrangementId, 'takes'],
      });
    },
  });
}

export function useDeleteTake(
  arrangementId: string,
): UseMutationResult<void, Error, { takeId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ takeId }: { takeId: string }) =>
      api.fetch<void>(`/takes/${takeId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['arrangements', arrangementId, 'takes'],
      });
    },
  });
}
